import { useEffect } from 'react'

/** Bundle hash currently referenced by the served index.html, or null. */
function parseBundleHash(html: string): string | null {
  const m = html.match(/\/assets\/index-([A-Za-z0-9_-]+)\.js/)
  return m ? m[1] : null
}

/** The hash this page actually booted with, read from its own <script> tags. */
function currentBundleHash(): string | null {
  for (const el of Array.from(document.getElementsByTagName('script'))) {
    const hash = parseBundleHash(el.getAttribute('src') ?? '')
    if (hash) return hash
  }
  return null
}

/**
 * Reload the kiosk when a new frontend build is deployed.
 *
 * The Pi runs Chromium once at boot (`--kiosk --app`) and nothing ever reloads
 * it. React Query keeps refreshing DATA, so new nodes and metrics appear —
 * but the JS stays whatever was served at boot. A UI fix therefore looked
 * "not deployed" on the wall display until someone physically rebooted the Pi.
 *
 * Polling index.html is deliberate: the asset hash is the build identity, so
 * this needs no version endpoint and cannot drift from what is actually served.
 * We only reload when both hashes parse AND differ, so a failed fetch or an
 * unexpected page shape can never cause a reload loop.
 */
export function useReloadOnNewBuild(pollMs = 10 * 60_000): void {
  useEffect(() => {
    const booted = currentBundleHash()
    if (!booted) return // unknown shape (dev server) — never reload

    let cancelled = false
    const check = async () => {
      try {
        const res = await fetch('/', { cache: 'no-store' })
        if (!res.ok) return
        const served = parseBundleHash(await res.text())
        if (!cancelled && served && served !== booted) {
          window.location.reload()
        }
      } catch {
        /* offline or API down — try again next tick */
      }
    }

    const id = setInterval(() => void check(), pollMs)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [pollMs])
}
